// src/components/MobileHeader.js
import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Badge, 
  TextField, 
  Box, 
  Typography, 
  InputAdornment,
  Button
} from '@mui/material';
import { ShoppingBag, Search } from '@mui/icons-material';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { supabase } from '../services/supabaseClient';
import SubNavigationBar from './SubNavigationBar'; // Import the SubNavigationBar

const MobileHeader = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localSearchTerm, setLocalSearchTerm] = useState(
    searchParams.get('search') || ''
  );
  const [categories, setCategories] = useState([]);
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine current main department
  const getCurrentDepartment = () => {
    const path = location.pathname;
    if (path.includes('/department/men')) return 'الرجال';
    if (path.includes('/department/women')) return 'النساء';
    if (path.includes('/department/children')) return 'الأطفال';
    return 'الأطفال';
  };
  const currentDepartment = getCurrentDepartment();

  // Fetch all categories from Supabase
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('id, name, parent_id')
          .order('id');
        if (error) throw error;
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Derive subcategories for the current department
  const subcategories = React.useMemo(() => {
    const mainCat = categories.find(
      (cat) => cat.parent_id === null && cat.name === currentDepartment
    );
    if (!mainCat) return [];
    return categories.filter((cat) => cat.parent_id === mainCat.id);
  }, [categories, currentDepartment]);

  // Update search query param when typing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);
      localSearchTerm
        ? newParams.set('search', localSearchTerm)
        : newParams.delete('search');
      setSearchParams(newParams);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [localSearchTerm, searchParams, setSearchParams]);

  const getDepartmentPath = (arabicName) => {
    switch (arabicName) {
      case 'الرجال':
        return 'men';
      case 'النساء':
        return 'women';
      case 'الأطفال':
        return 'children';
      default:
        return 'children';
    }
  };

  const handleDepartmentClick = (department) => {
    const path = `/department/${getDepartmentPath(department)}`;
    const params = new URLSearchParams();
    if (localSearchTerm) params.set('search', localSearchTerm);
    const fullPath = params.toString() ? `${path}?${params.toString()}` : path;
    navigate(fullPath);
  };

  const mainDepartments = ['الرجال', 'النساء', 'الأطفال'];

  return (
    <AppBar position="sticky" sx={{ backgroundColor: '#fff', color: '#333' }}>
      <Toolbar
        sx={{
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
          direction: 'ltr',
          pb: 0,
        }}
      >
        {/* First row: bag icon, title, and logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <IconButton
            edge="start"
            onClick={() => navigate('/cart')}
            sx={{ color: '#0f3662' }}
          >
            <Badge badgeContent={cartCount} color="secondary">
              <ShoppingBag />
            </Badge>
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            <Box component="span" sx={{ color: '#0f3662' }}>A to </Box>
            <Box component="span" sx={{ color: '#a72c2a' }}>Z</Box>
            <Box component="span" sx={{ color: '#0f3662' }}> Store</Box>
          </Typography>

          {/* Logo instead of toggle button */}
          <Box
            component="img"
            src="/logo192.png" // Update with your actual logo path
            alt="Store Logo"
            sx={{ height: 40, width: 40 }} // Adjust size as needed
          />
        </Box>

        {/* Second row: Main department navigation */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            gap: 0.5,
            width: '100%',
            px: 1
          }}
        >
          {mainDepartments.map((dept) => (
            <Button
              key={dept}
              onClick={() => handleDepartmentClick(dept)}
              size="small"
              sx={{
                flex: 1,
                minWidth: 0,
                color: currentDepartment === dept ? '#fff' : '#0f3662',
                backgroundColor: currentDepartment === dept ? '#0f3662' : 'transparent',
                fontWeight: 'bold',
                fontSize: '14px',
                textTransform: 'none',
                borderRadius: '5px',
                py: 0.5,
                mx: 0.25,
                border: currentDepartment === dept ? 'none' : '1px solid #0f3662',
                '&:hover': {
                  backgroundColor: currentDepartment === dept ? '#0f3662' : 'rgba(15, 54, 98, 0.1)',
                },
              }}
            >
              {dept}
            </Button>
          ))}
        </Box>

        {/* Third row: SubNavigationBar - directly rendered without wrapper */}
        {subcategories.length > 0 && (
          <SubNavigationBar 
            subcategories={subcategories}
            currentDepartmentPath={getDepartmentPath(currentDepartment)}
            currentDepartmentName={currentDepartment}
            variant="mobile"
          />
        )}

        {/* Fourth row: search field */}
        <Box sx={{ width: '100%', mb: 1 }}>
          <TextField
            fullWidth
            placeholder="بحث عن المنتج"
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            size="small"
            sx={{
              backgroundColor: 'white',
              borderRadius: '4px',
              '& .MuiInputBase-root': {
                direction: 'rtl',
              },
              '& .MuiInputBase-input': {
                textAlign: 'right', // Changed from right to left
                paddingRight: '8px', // Changed from paddingRight
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0f3662',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search sx={{ color: '#757575' }} />
                </InputAdornment>
              ),
              sx: {
                '& input::placeholder': {
                  opacity: 1,
                  color: '#757575',
                  textAlign: 'right', // Keep Arabic text right-aligned
                },
              }
            }}
          />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default MobileHeader;
